import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../../DataGridFlexSales';

import {
  reportCollectionsFilterSelector,
  reportAccountingInformationFilterSelector,
  reportFilterCountryCodeSelector,
  reportFilterSelector,
  reportPettyCashWithdrawalReportSelector,
  downloadReportPdf,
} from '../../../state';
import { PettyCashWithdrawalReport } from '../../../types';
import { getAccountingInformationReportPdfFilters, getRecaPdfParameters } from '../getPdfFilters';
import handlerFactory, { PettyCashWithdrawalReportDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<
    PettyCashWithdrawalReport,
    PettyCashWithdrawalReportDataGridContext
  >();

  const { t } = useTranslation();
  const title = t('accounting-info-report.petty-cash-withdrawal.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const reportPettyCashWithdrawalReportDataGridResource = useSelector(
    reportPettyCashWithdrawalReportSelector
  );
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);
  const accountingInformationFilters = useSelector(reportAccountingInformationFilterSelector);

  const { data, loading } = reportPettyCashWithdrawalReportDataGridResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
    }),
    [t, country]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: { ...reportCollectionsFilters, ...accountingInformationFilters },
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<
    PettyCashWithdrawalReport,
    PettyCashWithdrawalReportDataGridContext
  >();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportFilters = useSelector(reportFilterSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);
  const accountingInformationFilters = useSelector(reportAccountingInformationFilterSelector);

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: ['countryCode', 'stores', 'startBusinessDate', 'endBusinessDate'],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
              filters: getAccountingInformationReportPdfFilters(reportFilters, {
                ...reportCollectionsFilters,
                ...accountingInformationFilters,
              }),
              additionalParameters: getRecaPdfParameters(
                t,
                reportFilters,
                reportCollectionsFilters,
                []
              ),
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t, reportFilters, reportCollectionsFilters, accountingInformationFilters]);

  return null;
};

const PettyCashWithdrawalDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default PettyCashWithdrawalDataGrid;
